import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React, {useCallback} from 'react';

import staticURL from 'lib/staticURL';
import Accordion from 'ui-library/Accordion';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {Cell, CellRenderOptions, Column, Table} from 'ui-library/simple-table/SimpleTable';

import SingleFlowchartTransitionModal, {
  TransitionModalAction,
  TransitionType,
} from './transition-modal/SingleFlowchartTransitionModal';

export const PLANNED_TRANSITIONS_EMPTY_STATE_MESSAGE = 'Draw transitions in the flowchart to view them here';
export const OPTIONAL_TRANSITIONS_EMPTY_STATE_MESSAGE = 'Add optional transitions to view them here';

export interface FlowchartTransition {
  id: string;
  fromNodeName: string;
  fromNodeIcon?: BenchlingIconName;
  toNodeName: string;
  toNodeIcon?: BenchlingIconName;
}

export interface FlowchartTransitionsSidepanelContentProps {
  plannedTransitions: Array<FlowchartTransition>;
  optionalTransitions: Array<FlowchartTransition>;
  /** Called when a transition is edited/saved from the modal */
  onEditTransition: (transitionId: string) => void;
  /** Called when a new optional transition is created from the modal */
  onCreateTransition: () => void;
  /** Called when an optional transition is deleted */
  onDeleteTransition: (transitionId: string) => void;
  isLocked: boolean;
}

interface TransitionRowActionsProps {
  transitionId: string;
  isRowHovered: boolean;
  showDeleteButton: boolean;
  isLocked: boolean;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
}

function TransitionRowActions(props: TransitionRowActionsProps): JSX.Element {
  const {transitionId, isRowHovered, showDeleteButton, isLocked, onViewOrEditTransition, onDeleteTransition} =
    props;

  const editIcon = isLocked ? 'eye' : 'edit';
  const editTooltip = isLocked ? 'View transition' : 'Edit transition';

  return (
    <div className={css(styles.actionButtons, isRowHovered && styles.actionButtonsVisible)}>
      <TooltipWrapper tooltipTitle={editTooltip}>
        <Button
          buttonStyle={ButtonStyle.TRANSPARENT}
          size={ButtonSize.SMALL}
          icon={<BenchlingIcon icon={editIcon} className={css(styles.actionButtonIcon)} />}
          onClick={() => onViewOrEditTransition?.(transitionId)}
          className={css(styles.actionButton)}
          dataTestAttrs={{
            component: 'FlowchartTransitionsSidepanelContent',
            element: isLocked ? 'viewTransitionButton' : 'editTransitionButton',
            key: transitionId,
          }}
        />
      </TooltipWrapper>
      {showDeleteButton && !isLocked && (
        <TooltipWrapper tooltipTitle="Delete transition">
          <Button
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.SMALL}
            icon={<BenchlingIcon icon="delete" className={css(styles.actionButtonIcon)} />}
            onClick={() => onDeleteTransition?.(transitionId)}
            className={css(styles.actionButton)}
            dataTestAttrs={{
              component: 'FlowchartTransitionsSidepanelContent',
              element: 'deleteTransitionButton',
              key: transitionId,
            }}
          />
        </TooltipWrapper>
      )}
    </div>
  );
}

interface TransitionNodeColumnOptions {
  header: string;
  columnClassName: string;
  transitions: Array<FlowchartTransition>;
  getNodeName: (transition: FlowchartTransition) => string;
  getNodeIcon: (transition: FlowchartTransition) => BenchlingIconName | undefined;
}

/**
 * Render a column for a transition node.
 *
 * SimpleTable expects all children to be Column components, so using a function instead of making a new JSX.Element.
 */
function renderTransitionNodeColumn(options: TransitionNodeColumnOptions): JSX.Element {
  const {header, columnClassName, transitions, getNodeName, getNodeIcon} = options;

  return (
    <Column
      header={<Cell className={columnClassName}>{header}</Cell>}
      renderCell={(rowIdx: number) => {
        const transition = transitions[rowIdx];
        return (
          <Cell className={classnames(css(styles.wrapCellText), columnClassName)}>
            <Chip size={ChipSize.MINI} text={getNodeName(transition)} icon={getNodeIcon(transition)} />
          </Cell>
        );
      }}
    />
  );
}

interface TransitionsTableProps {
  transitions: Array<FlowchartTransition>;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
  showDeleteButton?: boolean;
  isLocked?: boolean;
}

function TransitionsTable(props: TransitionsTableProps): JSX.Element {
  const {
    transitions,
    onViewOrEditTransition,
    onDeleteTransition,
    showDeleteButton = false,
    isLocked = false,
  } = props;

  return (
    <div className={css(styles.tableWrapper)}>
      <Table
        numRows={transitions.length}
        calculateInView={false}
        fitContent={false}
        frozenHeader={false}
        getRowKey={(rowIndex) => transitions[rowIndex].id}
        includeNegativeMargins={false}
        isCompact={false}
        overflowVisible={true}
        tableLayout="fixed"
      >
        {renderTransitionNodeColumn({
          header: 'From',
          columnClassName: css(styles.fromColumn),
          transitions,
          getNodeName: (t) => t.fromNodeName,
          getNodeIcon: (t) => t.fromNodeIcon,
        })}
        {renderTransitionNodeColumn({
          header: 'To',
          columnClassName: css(styles.toColumn),
          transitions,
          getNodeName: (t) => t.toNodeName,
          getNodeIcon: (t) => t.toNodeIcon,
        })}
        <Column
          header={<Cell className={css(styles.actionColumn)} />}
          renderCell={(rowIdx: number, options: CellRenderOptions) => {
            const transition = transitions[rowIdx];
            return (
              <Cell className={css(styles.actionColumn)}>
                <TransitionRowActions
                  transitionId={transition.id}
                  isRowHovered={options.isRowHovered}
                  showDeleteButton={showDeleteButton}
                  isLocked={isLocked}
                  onViewOrEditTransition={onViewOrEditTransition}
                  onDeleteTransition={onDeleteTransition}
                />
              </Cell>
            );
          }}
        />
      </Table>
    </div>
  );
}

const EMPTY_TRANSITIONS_GRAPHIC_PATH = '/static/img/processes/two_node_flowchart.svg';

function EmptyTransitionsGraphic(): JSX.Element {
  return (
    <img src={staticURL(EMPTY_TRANSITIONS_GRAPHIC_PATH)} className={css(styles.emptyTransitionsGraphic)} />
  );
}

function EmptyTransitions({message}: {message: string}): JSX.Element {
  return (
    <div className={css(styles.emptyTransitions)}>
      <span className={css(styles.emptyTransitionsText)}>{message}</span>
      <EmptyTransitionsGraphic />
    </div>
  );
}

interface PlannedTransitionsSectionProps {
  transitions: Array<FlowchartTransition>;
  onViewOrEditTransition?: (transitionId: string) => void;
  isLocked?: boolean;
}

function PlannedTransitionsSection(props: PlannedTransitionsSectionProps): JSX.Element {
  const {transitions, onViewOrEditTransition, isLocked} = props;
  const hasTransitions = transitions.length > 0;

  return (
    <div className={css(styles.section)}>
      <Accordion
        header={<div className={css(styles.title)}>Planned transitions</div>}
        headerSpacing="none"
        isOpenByDefault={true}
        dataTestAttrs={{
          component: 'FlowchartTransitionsSidepanelContent',
          element: 'plannedTransitionsAccordion',
        }}
      >
        <div className={css(styles.sectionDescription)}>
          Transitions drawn in the flowchart will appear here. You can edit these transitions, but they can't
          be deleted.
        </div>
        {hasTransitions ? (
          <TransitionsTable
            transitions={transitions}
            onViewOrEditTransition={onViewOrEditTransition}
            isLocked={isLocked}
          />
        ) : (
          <EmptyTransitions message={PLANNED_TRANSITIONS_EMPTY_STATE_MESSAGE} />
        )}
      </Accordion>
    </div>
  );
}

interface OptionalTransitionsSectionProps {
  transitions: Array<FlowchartTransition>;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
  onAddTransition?: () => void;
  isLocked?: boolean;
}

function OptionalTransitionsSection(props: OptionalTransitionsSectionProps): JSX.Element {
  const {transitions, onViewOrEditTransition, onDeleteTransition, onAddTransition, isLocked} = props;
  const hasTransitions = transitions.length > 0;

  return (
    <div className={css(styles.section)}>
      <Accordion
        header={<div className={css(styles.title)}>Optional transitions</div>}
        isOpenByDefault={true}
        dataTestAttrs={{
          component: 'FlowchartTransitionsSidepanelContent',
          element: 'optionalTransitionsAccordion',
        }}
      >
        <div className={css(styles.sectionDescriptionWithButton)}>
          <span>
            Create transitions that users can draw while editing the flowchart, if editing is enabled.
          </span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            icon="plus"
            text="Add optional transition"
            onClick={onAddTransition}
            disabled={isLocked}
            dataTestAttrs={{
              component: 'FlowchartTransitionsSidepanelContent',
              element: 'addOptionalTransitionButton',
            }}
          />
        </div>
        {hasTransitions ? (
          <TransitionsTable
            transitions={transitions}
            onViewOrEditTransition={onViewOrEditTransition}
            onDeleteTransition={onDeleteTransition}
            showDeleteButton={true}
            isLocked={isLocked}
          />
        ) : (
          <EmptyTransitions message={OPTIONAL_TRANSITIONS_EMPTY_STATE_MESSAGE} />
        )}
      </Accordion>
    </div>
  );
}

export default function FlowchartTransitionsSidepanelContent(
  props: FlowchartTransitionsSidepanelContentProps
): JSX.Element {
  const {
    plannedTransitions,
    optionalTransitions,
    onEditTransition,
    onCreateTransition,
    onDeleteTransition,
    isLocked,
  } = props;

  const handleViewOrEditPlannedTransition = useCallback(
    (transitionId: string) => {
      runModal(SingleFlowchartTransitionModal, {
        action: isLocked ? TransitionModalAction.VIEW : TransitionModalAction.EDIT,
        type: TransitionType.PLANNED,
        transitionId,
        onRequestSubmit: () => {
          if (isLocked) {
            return;
          }
          onEditTransition(transitionId);
        },
      });
    },
    [isLocked, onEditTransition]
  );

  const handleViewOrEditOptionalTransition = useCallback(
    (transitionId: string) => {
      runModal(SingleFlowchartTransitionModal, {
        action: isLocked ? TransitionModalAction.VIEW : TransitionModalAction.EDIT,
        type: TransitionType.OPTIONAL,
        transitionId,
        onRequestSubmit: () => {
          if (isLocked) {
            return;
          }
          onEditTransition(transitionId);
        },
      });
    },
    [isLocked, onEditTransition]
  );

  const handleAddOptionalTransition = useCallback(() => {
    runModal(SingleFlowchartTransitionModal, {
      action: TransitionModalAction.ADD,
      type: TransitionType.OPTIONAL,
      onRequestSubmit: onCreateTransition,
    });
  }, [onCreateTransition]);

  return (
    <div className={css(styles.container)}>
      <PlannedTransitionsSection
        transitions={plannedTransitions}
        onViewOrEditTransition={handleViewOrEditPlannedTransition}
        isLocked={isLocked}
      />
      <div className={css(styles.divider)} />
      <OptionalTransitionsSection
        transitions={optionalTransitions}
        onViewOrEditTransition={handleViewOrEditOptionalTransition}
        onDeleteTransition={onDeleteTransition}
        onAddTransition={handleAddOptionalTransition}
        isLocked={isLocked}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
  },
  title: {
    paddingLeft: '4px',
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
  },
  section: {
    marginBottom: '24px',
  },
  divider: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    marginLeft: '-16px',
    marginRight: '-16px',
    marginTop: 0,
    marginBottom: 0,
  },
  sectionDescription: {
    fontSize: '14px',
    color: Color.GRAY_900,
    marginTop: '8px',
    marginBottom: '16px',
  },
  sectionDescriptionWithButton: {
    fontSize: '14px',
    color: Color.GRAY_900,
    marginTop: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  fromColumn: {
    width: 'calc(50% - 60px)',
  },
  toColumn: {
    width: 'calc(50% - 60px)',
  },
  actionColumn: {
    width: '120px',
    textAlign: 'right',
  },
  wrapCellText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  tableWrapper: {
    overflow: 'hidden',
    ':global': {
      '.simpleTable': {
        overflowX: 'visible',
      },
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '0px',
    visibility: 'hidden',
    justifyContent: 'flex-end',
  },
  actionButton: {
    padding: '4px',
  },
  actionButtonIcon: {
    color: Color.GRAY_600,
  },
  actionButtonsVisible: {
    visibility: 'visible',
  },
  emptyTransitions: {
    backgroundColor: Color.GRAY_50,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  emptyTransitionsGraphic: {
    backgroundColor: Color.GRAY_50,
    paddingTop: 16,
  },
  emptyTransitionsText: {
    width: '50%',
    textAlign: 'center',
  },
});
